import { shallowRef, defineComponent, watchEffect, computed } from 'vue';
import classNames from '../_util/classNames';
import PropTypes from '../_util/vue-types';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import Wave from '../_util/wave';
import { isPresetColor, isPresetStatusColor } from '../_util/colors';
import { eventType, objectType } from '../_util/type';
import CheckableTag from './CheckableTag';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import warning from '../_util/warning';
import useStyle from './style';
export const tagProps = () => ({
    prefixCls: String,
    color: {
        type: String,
    },
    closable: { type: Boolean, default: false },
    closeIcon: PropTypes.any,
    /** @deprecated `visible` will be removed in next major version. */
    visible: { type: Boolean, default: undefined },
    onClose: {
        type: Function,
    },
    onClick: eventType(),
    'onUpdate:visible': Function,
    icon: PropTypes.any,
    bordered: { type: Boolean, default: true },
    /** Semantic structure className (antd ≥ 5.23). */
    classNames: objectType(),
    /** Semantic structure style (antd ≥ 5.23). */
    styles: objectType(),
});
const Tag = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ATag',
    inheritAttrs: false,
    props: tagProps(),
    // emits: ['update:visible', 'close'],
    slots: Object,
    setup(props, { slots, emit, attrs }) {
        const { prefixCls, direction } = useConfigInject('tag', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const visible = shallowRef(true);
        // Warning for deprecated usage
        if (process.env.NODE_ENV !== 'production') {
            warning(props.visible === undefined, 'Tag', '`visible` is deprecated, please use `<Tag v-show="visible" />` instead.');
        }
        watchEffect(() => {
            if (props.visible !== undefined) {
                visible.value = props.visible;
            }
        });
        const handleCloseClick = (e) => {
            e.stopPropagation();
            emit('update:visible', false);
            emit('close', e);
            if (e.defaultPrevented) {
                return;
            }
            if (props.visible === undefined) {
                visible.value = false;
            }
        };
        // const isPresetColor = computed(() => {
        //   const { color } = props;
        //   if (!color) {
        //     return false;
        //   }
        //   return PresetColorRegex.test(color) || PresetStatusColorRegex.test(color);
        // });
        const isInternalColor = computed(() => isPresetColor(props.color) || isPresetStatusColor(props.color));
        const tagClassName = computed(() => classNames(prefixCls.value, hashId.value, {
            [`${prefixCls.value}-${props.color}`]: isInternalColor.value,
            [`${prefixCls.value}-has-color`]: props.color && !isInternalColor.value,
            [`${prefixCls.value}-hidden`]: !visible.value,
            [`${prefixCls.value}-rtl`]: direction.value === 'rtl',
            [`${prefixCls.value}-borderless`]: !props.bordered,
        }));
        const handleClick = (e) => {
            emit('click', e);
        };
        return () => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const { icon = (_a = slots.icon) === null || _a === void 0 ? void 0 : _a.call(slots), color, closeIcon = (_b = slots.closeIcon) === null || _b === void 0 ? void 0 : _b.call(slots), closable = false, } = props;
            const renderCloseIcon = () => {
                var _a, _b, _c, _d;
                if (closable) {
                    return closeIcon ? (<span class={[`${prefixCls.value}-close-icon`, (_a = props.classNames) === null || _a === void 0 ? void 0 : _a.closeIcon]} style={(_b = props.styles) === null || _b === void 0 ? void 0 : _b.closeIcon} onClick={handleCloseClick}>
              {closeIcon}
            </span>) : (<CloseOutlined class={[`${prefixCls.value}-close-icon`, (_c = props.classNames) === null || _c === void 0 ? void 0 : _c.closeIcon]} style={(_d = props.styles) === null || _d === void 0 ? void 0 : _d.closeIcon} onClick={handleCloseClick}/>);
                }
                return null;
            };
            const tagStyle = Object.assign({ backgroundColor: color && !isInternalColor.value ? color : undefined }, (_c = props.styles) === null || _c === void 0 ? void 0 : _c.root);
            const iconNode = icon || null;
            const children = (_d = slots.default) === null || _d === void 0 ? void 0 : _d.call(slots);
            const contentNode = ((_e = props.classNames) === null || _e === void 0 ? void 0 : _e.content) || ((_f = props.styles) === null || _f === void 0 ? void 0 : _f.content) ? (<span class={(_g = props.classNames) === null || _g === void 0 ? void 0 : _g.content} style={(_h = props.styles) === null || _h === void 0 ? void 0 : _h.content}>
            {children}
          </span>) : (children);
            const kids = iconNode ? (<>
          {((_j = props.classNames) === null || _j === void 0 ? void 0 : _j.icon) || ((_k = props.styles) === null || _k === void 0 ? void 0 : _k.icon) ? (<span class={(_l = props.classNames) === null || _l === void 0 ? void 0 : _l.icon} style={(_m = props.styles) === null || _m === void 0 ? void 0 : _m.icon}>
              {iconNode}
            </span>) : (iconNode)}
          {((_o = props.classNames) === null || _o === void 0 ? void 0 : _o.content) || ((_p = props.styles) === null || _p === void 0 ? void 0 : _p.content) ? (contentNode) : (<span>{children}</span>)}
        </>) : (contentNode);
            const isNeedWave = props.onClick !== undefined;
            const tagNode = (<span {...attrs} onClick={handleClick} class={[tagClassName.value, (_q = props.classNames) === null || _q === void 0 ? void 0 : _q.root, attrs.class]} style={[tagStyle, attrs.style]}>
          {kids}
          {renderCloseIcon()}
        </span>);
            return wrapSSR(isNeedWave ? <Wave>{tagNode}</Wave> : tagNode);
        };
    },
});
Tag.CheckableTag = CheckableTag;
Tag.install = function (app) {
    app.component(Tag.name, Tag);
    app.component(CheckableTag.name, CheckableTag);
    return app;
};
export { CheckableTag };
export default Tag;
